import { Input, TextArea, TextAreaHandle } from "@progress/kendo-react-inputs";

import { Label, Error, Hint } from "@progress/kendo-react-labels";
import parse from 'html-react-parser'
import { useEffect, useRef, useState } from "react";
import { SurveyEngine_Models_Answer, SurveyEngine_Models_BaseResponse, SurveyEngine_Models_GetValueResponse } from "../../../api";
import { useGlobalCtx } from "../../../Context/GlobalContext";
import { ListItemProps, MultiSelect, MultiSelectChangeEvent, MultiSelectFilterChangeEvent, MultiSelectHandle } from "@progress/kendo-react-dropdowns";
import { filterBy } from "@progress/kendo-data-query";
import { InputLayout } from "./GetValue";
import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleExclamation, faCircleCheck, faLightbulbOn } from '@fortawesome/pro-regular-svg-icons';
import { DatePicker, DatePickerChangeEvent, TimePickerChangeEvent } from "@progress/kendo-react-dateinputs";
import { TimePicker } from '@progress/kendo-react-dateinputs';
import { IntlProvider, load, LocalizationProvider } from '@progress/kendo-react-intl';
import likelySubtags from 'cldr-core/supplemental/likelySubtags.json';
import { parseDate } from "@progress/kendo-intl";
load(likelySubtags);

export interface GetValueInputProps {
    id: number,
    question: string,
    confirmQuestion?: string
    type: string,
    positiveOnly: boolean,
    alphaNumericOnly?: boolean,
    layout: InputLayout,
    minLength: number,
    maxLength: number,
    answers?: SurveyEngine_Models_Answer[]
    defaultAnswer: string,
    caseSensitive: boolean,
    charCount: boolean,
    isError: boolean,
    focus: boolean,
    validationType: string,
    textboxHeight: number,
    callback: (response: SurveyEngine_Models_BaseResponse) => void
}

export const GetValueInput = (props: GetValueInputProps) => {
    const globalCTX = useGlobalCtx();
    const languageCode = globalCTX.Language.split('-')[0];

    load(require(`cldr-dates-full/main/${languageCode}/ca-gregorian.json`));
    load(require(`cldr-dates-full/main/${languageCode}/dateFields.json`));
    load(require('cldr-core/supplemental/weekData.json'));

    const [isError, setIsError] = useState(props.isError);

    const [value, setValue] = useState<string>(
        props.layout !== InputLayout.MULTI_SELECT ? props.defaultAnswer : ""
    );

    //multi select
    const [values, setValues] = useState<string[]>(
        props.layout === InputLayout.MULTI_SELECT ?
            props.answers?.filter((answer) => answer.isSelected).map((item) => item.message) as string[] : [""]
    );

    const [answersData, setAnswersData] = useState<string[] | undefined>(
        props.layout === InputLayout.MULTI_SELECT ? props.answers?.map((f) => f.message) as string[] : undefined
    );
    const [confirmValue, setConfirmValue] = useState<string | undefined>(props.defaultAnswer);
    const [touched, setTouched] = useState(false);
    const [confirmTouched, setConfirmTouched] = useState(false);
    const [match, setMatch] = useState(value && confirmValue ? value === confirmValue : false);


    const labelId = `${props.id}_label`
    const InputId = `${props.id}_Input`
    const confirmLabelId = `${props.id}_confirmLabel`
    const confirmInputId = `${props.id}_confirmInput`

    const errorId = `${props.id}_error`
    const confirmErrorId = `${props.id}_confirmError`

    const gvNumericRef = useRef<Input>(null);
    const gvNumericConfirmRef = useRef<Input>(null);
    const gvTextAreaRef = useRef<TextAreaHandle>(null);
    const gvTextAreaConfirmRef = useRef<TextAreaHandle>(null);
    const gvMultiSelectRef = useRef<MultiSelectHandle>(null);

    const isDate = props.validationType.toLowerCase() === 'date'
    const isBirthDate = props.validationType.toLowerCase() === 'birthdate';
    const isTime = props.validationType.toLowerCase() === 'time';

    useEffect(() => {
        const setFocusToInputRef = () => {
            if (props.confirmQuestion) {
                if ((props.defaultAnswer && props.defaultAnswer.length > 0 && !match && gvNumericConfirmRef) || 
                (value && value.length > 0 && !match && gvNumericConfirmRef) ||
                match) {
                    gvNumericConfirmRef.current?.focus();
                }
                else {
                    gvNumericRef.current?.focus();
                }
            }
            else if (gvNumericRef) {
                gvNumericRef.current?.focus();
            }
        }
        const setFocusToTextAreaRef = () => {
            if (props.confirmQuestion) {
                if ((props.defaultAnswer && props.defaultAnswer.length > 0 && !match && gvTextAreaConfirmRef) || 
                (value && value.length > 0 && !match && gvTextAreaConfirmRef) ||
                match) {
                    gvTextAreaConfirmRef.current?.focus();
                }
                else {
                    gvTextAreaRef.current?.element?.current?.focus();
                }
            }
            else if (gvTextAreaRef) {
                gvTextAreaRef.current?.element?.current?.focus();
            }
        }
        const setFousToMultiSelectRef = () => {
            (gvMultiSelectRef.current?.element?.querySelector('[role="combobox"]') as HTMLInputElement).focus();
        }


        //setIsError(props.isError);
        if (props.focus) {
            if (props.layout === InputLayout.SINGLE_LINE) {
                setFocusToInputRef();
            }
            else if (props.layout === InputLayout.MULTI_LINE) {
                setFocusToTextAreaRef();
            }
            else if (props.layout === InputLayout.MULTI_SELECT) {
                setFousToMultiSelectRef();
            }
        }

        if (props.defaultAnswer && props.defaultAnswer.length >= props.minLength && props.defaultAnswer.length <= props.maxLength) {
            let response = {
                commandType: "GetValue",
                commandIndex: props.id,
                value: props.defaultAnswer
            };
            props.callback(response);
            setValue(props.defaultAnswer);
        }
        else if (props.defaultAnswer.length > 0 && (props.defaultAnswer.length < props.minLength || props.defaultAnswer.length > props.maxLength)) {
            setIsError(true)
        }
        else { // Default answer is blank. This block will execute when the component loads first time 
            // or during a re-render when no default value is provided.
            if (props.minLength <= 0) {
                let response = {
                    commandType: 'GetValue',
                    commandIndex: props.id,
                    value: props.defaultAnswer ? props.defaultAnswer : '',
                }
                props.callback(response)
            }

            //If the input has been touched and the min/max validation is not met, set an error.
            if (touched && value.trim() === '' && props.defaultAnswer?.length <= 0 && (props.defaultAnswer.length < props.minLength || props.defaultAnswer.length > props.maxLength)) {
                setIsError(true)
            }
            else if (touched && value.trim() !== '' && value.trim() !== confirmValue?.trim()) {
                setMatch(false);
                setConfirmTouched(true);
            }
            else {
                setIsError(props.isError)
            }

            if (props.defaultAnswer?.length <= 0 && props.isError === false) {
                setValue('');
                setConfirmValue('');
                setMatch(false);
                setTouched(false);
            }
        }

    }, [props])// eslint-disable-line react-hooks/exhaustive-deps


    const checkDataType = (value: string) => {
        if (props.type === "number") {
            if (props.positiveOnly) {
                value = value.replace('-', '');
            }
            if (value.length > props.maxLength) {
                value = value.substring(0, props.maxLength);
            }
        }
        if (props.type === 'text' && props.validationType.toLocaleLowerCase() === 'zipcode') {
            value = value.replace(/[^\p{L}\p{M}0-9\s-]+$/gu, '');
        }
        if (props.type === 'tel' && props.validationType.toLocaleLowerCase() === 'phonenumber') {
            value = value.replace(/[^0-9]/g, '');
        }
        if (props.alphaNumericOnly) {
            value = value.replace(/[^a-zA-Z0-9\s]/g, '');
        }

        return value;
    }
    const checkInputValidity = (ev: any) => {
        if (props.type === "email") {
            const emailRegex = /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/;
            const isPatternValid = emailRegex.test(ev.target.value);
            return isPatternValid && ev.target.validity.valid;
        }
        if (props.validationType.toLocaleLowerCase() === "birthdate") {
            const dateInput = new Date(ev.target.value).toISOString().split('T')[0];
            const todayDate = new Date().toISOString().split('T')[0];
            return dateInput < todayDate;
        }
        else {
            return true;
        }
    }
    const handleDateChange = (event: DatePickerChangeEvent) => {
        HandleGetValueChange(event.value?.toLocaleDateString('en'), event);
    };
    const HandleTimeValueChange = (event: TimePickerChangeEvent) => {
        if (event.target.value !== null) {
            var timeVal = new Date(event.target.value ?? '').toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit', hour12: true })
            HandleGetValueChange(timeVal, event)
        }
        else {
            HandleGetValueChange('', event)
        }
    };
    const handleConfirmDateChange = (event: DatePickerChangeEvent) => {
        HandleConfirmValueChange(event.value?.toLocaleDateString('en'));
    };
    const HandleConfirmTimeValueChange = (event: TimePickerChangeEvent) => {
        if (event.target.value !== null) {
            var timeVal = new Date(event.target.value ?? "").toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit', hour12: true });
            HandleConfirmValueChange(timeVal);
        }
        else {
            HandleConfirmValueChange('')
        }
    };
    const HandleGetValueChange = (e: string | undefined, ev: any) => {
        let response: SurveyEngine_Models_GetValueResponse = {
            commandType: "Remove",
            commandIndex: props.id,
            value: "-1"
        };
        if (e) {
            e = checkDataType(e);
            let inputValid = checkInputValidity(ev);
            if (!inputValid) {
                setIsError(true);
            }
            else if ((e.length < props.minLength || e.length > props.maxLength)) {
                setIsError(true);
            }
            else {
                setIsError(false);
                if (!props.confirmQuestion) {
                    if (e?.length === 0 && props.minLength === 0) {
                        response = {
                            commandType: "GetValue",
                            commandIndex: props.id,
                            value: "0"
                        }

                    }
                    else {
                        response = {
                            commandType: "GetValue",
                            commandIndex: props.id,
                            value: e
                        }
                    }
                }
                else {
                    if (e !== confirmValue) {
                        setMatch(false);
                    }
                    else {
                        if (e && confirmValue && e === confirmValue) {
                            setMatch(true);
                            response = {
                                commandType: "GetValue",
                                commandIndex: props.id,
                                value: e
                            }
                        }
                    }

                }
            }
        }
        props.callback(response);
        setValue(e ?? "");
    }
    const HandleConfirmValueChange = (e: string | undefined) => {
        let response: SurveyEngine_Models_GetValueResponse = {
            commandType: "Remove",
            commandIndex: props.id,
            value: "-1"
        };
        if (e) {
            e = checkDataType(e);
        }

        if (props.caseSensitive ? value !== e : value?.toLocaleLowerCase() !== e?.toLocaleLowerCase()) {
            setMatch(false);
        }
        else {
            if (value?.length === 0 && props.minLength === 0) {
                response = {
                    commandType: "GetValue",
                    commandIndex: props.id,
                    value: "0"
                }
            }
            else {
                if (e && value && !(value.length < props.minLength || value.length > props.maxLength)) {
                    response = {
                        commandType: "GetValue",
                        commandIndex: props.id,
                        value: e
                    }
                }
            }

            setMatch(true);
        }
        props.callback(response);
        setConfirmValue(e);
    }
    const HandleMultiSelectChange = (event: MultiSelectChangeEvent) => {
        let response: SurveyEngine_Models_GetValueResponse = {
            commandType: "Remove",
            commandIndex: props.id,
            value: "-1"
        };

        response = {
            commandType: "GetValue",
            commandIndex: props.id,
            value: event.value?.join("][")
        }

        props.callback(response);

        setValues([...event.value]);
    };

    const HandleMultiSelectFilterChange = (event: MultiSelectFilterChangeEvent) => {
        if (event.filter.value.trim() !== '') {
            setAnswersData(filterBy(props.answers?.map((f) => f.message) as string[], event.filter));
        }
        else {
            clearMultiSelectFilter();
        }
    };
    const clearMultiSelectFilter = () => {
        setAnswersData(props.answers?.map((f) => f.message) as string[]);
    }
    const isInputValid = () => {
        if (value && !isError && value.length >= props.minLength && value.length <= props.maxLength)
            return true
        else
            return false
    }
    function HintOrError() {
        switch (props.type) {
            case "number":
                if ((touched && !value && props.minLength > 0) || isError) {
                    return <div className="flex flex-row">
                        <Error className={`theme-validator-error-text font-semibold ${props.charCount ? "basis-3/4" : "basis-full"}`} direction='start' id={errorId}>
                            <FontAwesomeIcon icon={faCircleExclamation} className="theme-icon-spacer" />
                            {globalCTX.LangResources.getValueMsg.numeric.error.replace('{min}', props.minLength).replace('{max}', props.maxLength)}
                        </Error>
                        {props.charCount && <Hint className="theme-validator-hint-text font-semibold basis-1/4" direction='end' id={errorId}>
                            <FontAwesomeIcon icon={faLightbulbOn} className="theme-icon-spacer" />{value ? value.length : 0}/{props.maxLength}
                        </Hint>}
                    </div>
                }
                else {
                    return <div className="flex flex-row">
                        <Hint className={`${isInputValid() ? "theme-validator-success-text" : "theme-validator-hint-text"} font-semibold ${props.charCount ? "basis-3/4" : "basis-full"}`} direction='start' id={errorId}>
                            <FontAwesomeIcon icon={faLightbulbOn} className="theme-icon-spacer" />{globalCTX.LangResources.getValueMsg.numeric.hint.replace('{min}', props.minLength).replace('{max}', props.maxLength)}
                        </Hint>
                        {props.charCount && <Hint className="theme-validator-hint-text font-semibold basis-1/4" direction='end' id={errorId}>
                            <FontAwesomeIcon icon={faLightbulbOn} className="theme-icon-spacer" />{value ? value.length : 0}/{props.maxLength}
                        </Hint>}
                    </div>
                }
            case "password":
                if ((touched && !value) || isError)
                    return <div className="flex flex-row">
                        <Error className={`theme-validator-error-text font-semibold ${props.charCount ? "basis-3/4" : "basis-full"}`} direction='start' id={errorId}>
                            <FontAwesomeIcon icon={faCircleExclamation} className="theme-icon-spacer" />
                            {globalCTX.LangResources.getValueMsg.password.error.replace('{min}', props.minLength).replace('{max}', props.maxLength)}
                        </Error>
                        {props.charCount && <Hint className="theme-validator-hint-text font-semibold basis-1/4" direction='end' id={errorId}>
                            <FontAwesomeIcon icon={faLightbulbOn} className="theme-icon-spacer" />{value ? value.length : 0}/{props.maxLength}
                        </Hint>}
                    </div>
                else
                    return <div className="flex flex-row">
                        <Hint className={`${isInputValid() ? "theme-validator-success-text" : "theme-validator-hint-text"} font-semibold ${props.charCount ? "basis-3/4" : "basis-full"}`} direction='start' id={errorId}>
                            <FontAwesomeIcon icon={faLightbulbOn} className="theme-icon-spacer" /> {globalCTX.LangResources.getValueMsg.password.error.replace('{min}', props.minLength).replace('{max}', props.maxLength)}
                        </Hint>
                        {props.charCount && <Hint className="theme-validator-hint-text font-semibold basis-1/4" direction='end' id={errorId}>
                            <FontAwesomeIcon icon={faLightbulbOn} className="theme-icon-spacer" />{value ? value.length : 0}/{props.maxLength}
                        </Hint>}
                    </div>
            case "date":
            case "birthdate":
                if ((touched && !value) || isError)
                    return <div className="flex flex-row">
                        <Error className={`theme-validator-error-text font-semibold ${props.charCount ? "basis-3/4" : "basis-full"}`} direction='start' id={errorId}>
                            <FontAwesomeIcon icon={faCircleExclamation} className="theme-icon-spacer" />{globalCTX.LangResources.getValueMsg.date.error}
                        </Error>
                        {props.charCount && <Hint className="theme-validator-hint-text font-semibold basis-1/4" direction='end' id={errorId}>
                            <FontAwesomeIcon icon={faLightbulbOn} className="theme-icon-spacer" />{value ? value.length : 0}/{props.maxLength}
                        </Hint>}
                    </div>
                else if (props.charCount)
                    return <Hint className="theme-validator-hint-text font-semibold" direction='end' id={errorId}><FontAwesomeIcon icon={faLightbulbOn} className="theme-icon-spacer" />{value ? value.length : 0}/{props.maxLength}</Hint>
                break;
            case "time":
                if ((touched && !value) || isError)
                    return <div className="flex flex-row">
                        <Error className={`theme-validator-error-text font-semibold ${props.charCount ? "basis-3/4" : "basis-full"}`} direction='start' id={errorId}>
                            <FontAwesomeIcon icon={faCircleExclamation} className="theme-icon-spacer" />{globalCTX.LangResources.getValueMsg.time.error}
                        </Error>
                        {props.charCount && <Hint className="theme-validator-hint-text font-semibold basis-1/4" direction='end' id={errorId}>
                            <FontAwesomeIcon icon={faLightbulbOn} className="theme-icon-spacer" />{value ? value.length : 0}/{props.maxLength}
                        </Hint>}
                    </div>
                else if (props.charCount)
                    return <Hint className="theme-validator-hint-text font-semibold" direction='end' id={errorId}><FontAwesomeIcon icon={faLightbulbOn} className="theme-icon-spacer" />{value ? value.length : 0}/{props.maxLength}</Hint>
                break;
            case "text":
            case "tel":
                if (props.layout !== InputLayout.MULTI_SELECT) {
                    if ((touched && !value && props.minLength > 0) || isError) {
                        return <div className="flex flex-row">
                            <Error className={`theme-validator-error-text font-semibold ${props.charCount ? "basis-3/4" : "basis-full"}`} direction='start' id={errorId}>
                                <FontAwesomeIcon icon={faCircleExclamation} className="theme-icon-spacer" />{globalCTX.LangResources.getValueMsg.text.error.replace('{min}', props.minLength).replace('{max}', props.maxLength)}
                            </Error>
                            {props.charCount && <Hint className="theme-validator-hint-text font-semibold basis-1/4" direction='end' id={errorId}>
                                <FontAwesomeIcon icon={faLightbulbOn} className="theme-icon-spacer" />{value ? value.length : 0}/{props.maxLength}
                            </Hint>}
                        </div>;
                    }
                    else {
                        return <div className="flex flex-row">
                            <Hint className={`${isInputValid() ? "theme-validator-success-text" : "theme-validator-hint-text"} font-semibold ${props.charCount ? "basis-3/4" : "basis-full"}`} direction='start' id={errorId}>
                                <FontAwesomeIcon icon={faLightbulbOn} className="theme-icon-spacer" />{globalCTX.LangResources.getValueMsg.text.hint.replace('{min}', props.minLength).replace('{max}', props.maxLength)}
                            </Hint>
                            {props.charCount && <Hint className="theme-validator-hint-text font-semibold basis-1/4" direction='end' id={errorId}>
                                <FontAwesomeIcon icon={faLightbulbOn} className="theme-icon-spacer" />{value ? value.length : 0}/{props.maxLength}
                            </Hint>}
                        </div>
                    }
                }
                else {
                    return '';
                }
            case "email":
                if ((touched && !value) || isError)
                    return <div className="flex flex-row">
                        <Error className={`theme-validator-error-text font-semibold ${props.charCount ? "basis-3/4" : "basis-full"}`} direction='start' id={errorId}>
                            <FontAwesomeIcon icon={faCircleExclamation} className="theme-icon-spacer" />{globalCTX.LangResources.getValueMsg.email.error}
                        </Error>
                    </div>
                else if (props.charCount)
                    return <Hint className="theme-validator-hint-text font-semibold" direction='end' id={errorId}><FontAwesomeIcon icon={faLightbulbOn} className="theme-icon-spacer" />{value ? value.length : 0}/{props.maxLength}</Hint>
                break;
        }

    }
    const itemRender = (
        li: React.ReactElement<HTMLLIElement>,
        itemProps: ListItemProps
    ) => {
        const itemChildren = (
            parse(itemProps.dataItem.replace(/""/g, '"'))
        );

        return React.cloneElement(li, li.props, itemChildren);
    };
    const getFullDateTime = (timeVal: string) => {
        var today = new Date();
        var todayString = today.toISOString().split('T')[0];
        return new Date(todayString + ' ' + timeVal);
    }
    const isValidNumericOrMinusKey = (e: any) => {
        const { key, ctrlKey } = e;
        return /[0-9]/.test(key) || key === '-' ||
            ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(key) ||
            (ctrlKey && (key === 'a' || key === 'c' || key === 'v' || key === 'x'));
    };
    const isValidPositiveOnlyKey = (e: any) => {
        const { key, ctrlKey } = e;
        return /[0-9]/.test(key)
            || ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(key) ||
            (ctrlKey && (key === 'a' || key === 'c' || key === 'v' || key === 'x'));
    };
    const HandleKeyDown = (e: any) => {
        const isPostiveOnly = props.type.toLowerCase() === 'number' && props.positiveOnly;
        const isNumeric = props.type.toLowerCase() === 'number' && !props.positiveOnly;
        if (isPostiveOnly && !isValidPositiveOnlyKey(e)) { e.preventDefault(); }
        if (isNumeric && !isValidNumericOrMinusKey(e)) { e.preventDefault(); }
    };
    return (
        <div className="theme-get-value mb-3 w-full">
            <Label
                className="theme-question text-xl font-semibold w-full block"
                id={labelId}
                editorId={InputId}
            >
                {parse(props.question.replace(/""/g, '"'))}
            </Label>

            {props.layout === InputLayout.SINGLE_LINE &&
                (isDate ? (
                    <LocalizationProvider language={languageCode}>
                        <IntlProvider locale={languageCode}>
                            <DatePicker
                                className="theme-answer w-full block"
                                formatPlaceholder={{ month: 'mm', year: 'yyyy', day: 'dd' }}
                                format={{ month: '2-digit', year: 'numeric', day: '2-digit' }}
                                value={value === '' ? null : parseDate(value.toString())}
                                id={InputId}
                                ref={gvNumericRef}
                                ariaDescribedBy={errorId}
                                onBlur={() => setTouched(true)}
                                onChange={handleDateChange}
                            />
                        </IntlProvider>
                    </LocalizationProvider>
                ) : isBirthDate ? (
                    <LocalizationProvider language={languageCode}>
                        <IntlProvider locale={languageCode}>
                            <DatePicker
                                className="theme-answer w-full block"
                                formatPlaceholder={{ month: 'mm', year: 'yyyy', day: 'dd' }}
                                format={{ month: '2-digit', year: 'numeric', day: '2-digit' }}
                                value={value === '' ? null : parseDate(value.toString())}
                                id={InputId}
                                ref={gvNumericRef}
                                ariaDescribedBy={errorId}
                                onBlur={() => setTouched(true)}
                                onChange={handleDateChange}
                                max={new Date()}
                            />
                        </IntlProvider>
                    </LocalizationProvider>
                ) :
                    isTime ? (
                        <LocalizationProvider language={languageCode}>
                            <IntlProvider locale={languageCode}>
                                <TimePicker
                                    placeholder='--:-- --'
                                    formatPlaceholder={{ hour: '--', minute: '--' }}
                                    value={value === '' ? null : getFullDateTime(value)}
                                    nowButton={false}
                                    className="theme-answer w-full block"
                                    ref={gvNumericRef}
                                    id={InputId}
                                    ariaDescribedBy={errorId}
                                    onBlur={() => setTouched(true)}
                                    onChange={HandleTimeValueChange} />
                            </IntlProvider>
                        </LocalizationProvider>
                    ) : (
                        <Input
              className = "theme-answer w-full block"
                            ref={gvNumericRef}
                            type={props.type}
                            id={InputId}
                            value={value}
                            min={props.positiveOnly ? 0 : undefined}
                            minLength={props.minLength}
                            maxLength={props.maxLength}
                            ariaDescribedBy={errorId}
                            valid={true}
                            onChange={(e) => HandleGetValueChange(e.value?.toString(), e)}
                            onBlur={() => setTouched(true)}
                            onKeyDown={HandleKeyDown}
                        />
                    ))}
            {props.layout === InputLayout.MULTI_LINE && (
                <TextArea
                    autoSize={true}
                    rows={props.textboxHeight}
                    className="theme-answer w-full block"
                    ref={gvTextAreaRef}
                    id={InputId}
                    value={value ?? ''}
                    minLength={props.minLength}
                    maxLength={props.maxLength}
                    ariaDescribedBy={errorId}
                    onChange={(e) => HandleGetValueChange(e.value?.toString(), e)}
                    onBlur={() => setTouched(true)}
                />
            )}
            {props.layout === InputLayout.MULTI_SELECT && (
                <MultiSelect
                    className="theme-answer w-full block"
                    ref={gvMultiSelectRef}
                    data={answersData}
                    value={values ?? ['']}
                    filterable={true}
                    onFilterChange={HandleMultiSelectFilterChange}
                    onChange={HandleMultiSelectChange}
                    onClose={clearMultiSelectFilter}
                    itemRender={itemRender}
                />
            )}
            <>{HintOrError()}</>
            {props.confirmQuestion && (
                <div className="pt-8">
                    <Label
                        className="theme-question text-xl font-semibold w-full block"
                        id={confirmLabelId}
                        editorId={confirmInputId}
                    >
                        {parse(props.confirmQuestion.replace(/""/g, '"'))}
                    </Label>
                    {props.layout === InputLayout.SINGLE_LINE &&
                        (isDate ? (
                            <LocalizationProvider language={languageCode}>
                                <IntlProvider locale={languageCode}>
                                    <DatePicker
                                        className="theme-answer w-full block"
                                        formatPlaceholder={{ month: 'mm', year: 'yyyy', day: 'dd' }}
                                        format={{ month: '2-digit', year: 'numeric', day: '2-digit' }}
                                        value={confirmValue === '' ? null : parseDate(confirmValue ? confirmValue.toString() : "")}
                                        id={confirmInputId}
                                        ref={gvNumericConfirmRef}
                                        ariaDescribedBy={confirmErrorId}
                                        onBlur={() => setConfirmTouched(true)}
                                        onChange={handleConfirmDateChange}
                                    />
                                </IntlProvider>
                            </LocalizationProvider>
                        ) : isBirthDate ? (
                            <LocalizationProvider language={languageCode}>
                                <IntlProvider locale={languageCode}>
                                    <DatePicker
                                        className="theme-answer w-full block"
                                        formatPlaceholder={{ month: 'mm', year: 'yyyy', day: 'dd' }}
                                        format={{ month: '2-digit', year: 'numeric', day: '2-digit' }}
                                        value={confirmValue === '' ? null : parseDate(confirmValue ? confirmValue.toString() : "")}
                                        id={confirmInputId}
                                        ref={gvNumericConfirmRef}
                                        ariaDescribedBy={confirmErrorId}
                                        onBlur={() => setConfirmTouched(true)}
                                        onChange={handleConfirmDateChange}
					max={new Date()}
                                    />
                                </IntlProvider>
                            </LocalizationProvider>
                        ) :
                            isTime ? (
                                <LocalizationProvider language={languageCode}>
                                    <IntlProvider locale={languageCode}>
                                        <TimePicker
                                            placeholder='--:-- --'
                                            formatPlaceholder={{ hour: '--', minute: '--' }}
                                            value={confirmValue === '' ? null : getFullDateTime(confirmValue ? confirmValue : "")}
                                            nowButton={false}
                                            className="theme-answer w-full block"
                                            ref={gvNumericConfirmRef}
                                            id={confirmInputId}
                                            ariaDescribedBy={confirmErrorId}
                                            onBlur={() => setConfirmTouched(true)}
                                            onChange={HandleConfirmTimeValueChange}
                                        />
                                    </IntlProvider>
                                </LocalizationProvider>
                            ) : (
                                <Input
                                    className="theme-answer w-full block"
                                    ref={gvNumericConfirmRef}
                                    type={props.type}
                                    id={confirmInputId}
                                    value={confirmValue}
                                    min={props.positiveOnly ? 0 : undefined}
                                    minLength={props.minLength}
                                    maxLength={props.maxLength}
                                    ariaDescribedBy={confirmErrorId}
                                    valid={true}
                                    onChange={(e) => HandleConfirmValueChange(e.value?.toString())}
                                    onBlur={() => setConfirmTouched(true)}
                                    onKeyDown={HandleKeyDown}
                                />
                            ))}
                    {props.layout === InputLayout.MULTI_LINE && (
                        <TextArea
                            autoSize={true}
                            rows={props.textboxHeight}
                            className="theme-answer w-full block"
                            ref={gvTextAreaConfirmRef}
                            id={confirmInputId}
                            value={confirmValue}
                            minLength={props.minLength}
                            maxLength={props.maxLength}
                            ariaDescribedBy={confirmErrorId}
                            onChange={(e) => HandleConfirmValueChange(e.value?.toString())}
                            onBlur={() => setTouched(true)}
                        />
                    )}
                    {confirmTouched && !match ? (
                        <Error
                            className="theme-validator-error-text font-semibold"
                            direction="start"
                            id={confirmErrorId}
                        >
                            <FontAwesomeIcon
                                icon={faCircleExclamation}
                                className="theme-icon-spacer"
                            />
                            {globalCTX.LangResources.getValueMsg.confirmValueError}
                        </Error>
                    ) : match ? (
                        <Hint
                            className="theme-validator-success-text font-semibold"
                            direction="start"
                            id={confirmErrorId}
                        >
                            <FontAwesomeIcon
                                icon={faCircleCheck}
                                className="theme-icon-spacer"
                            />
                            {globalCTX.LangResources.getValueMsg.confirmValueSuccess}
                        </Hint>
                    ) : undefined}
                </div>
            )}
        </div>
    )
}